import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import UnderConstruction from "./components/UnderConstruction";
import VolunteerPage from "./components/VolunteerPage";
import { 
  Menu, 
  X, 
  Heart, 
  Calendar, 
  ChevronRight,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Youtube,
  ArrowRight,
  Clock,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rich's new photos
const RICH_PHOTOS = {
  main: "https://customer-assets.emergentagent.com/job_e-commerce-585/artifacts/p87zr5of__B130024%20copy.jpg",
  seated: "https://customer-assets.emergentagent.com/job_e-commerce-585/artifacts/gg4mzc8b__B130009%20copy.jpg",
  chair: "https://customer-assets.emergentagent.com/job_e-commerce-585/artifacts/wir3isce__B130048%20copy.jpg"
};

// TikTok icon component
const TikTok = ({ size = 24, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

// Logo component
const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_0db52cda-c255-4bce-bd64-49ec29fdf268/artifacts/klev6xpu_LOGO%20-%20WEB%20VERSION.png" 
    alt="The Rich Porch" 
    style={{ height: '120px', width: 'auto', maxWidth: '400px', objectFit: 'contain' }}
  />
);

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#why", label: "Why This Exists" },
    { href: "#what-we-do", label: "What We Do" },
    { href: "#get-help", label: "Get Help" },
    { href: "#events", label: "Events" },
    { href: "/volunteer", label: "Volunteer", route: true },
    { href: "#founder", label: "Our Founder" },
    { href: "#sponsors", label: "Sponsors" },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      style={{
        position: 'fixed',
        top: 0,
        width: '100%',
        zIndex: 50,
        transition: 'all 0.3s ease',
        backgroundColor: scrolled ? 'rgba(253, 251, 247, 0.98)' : 'rgba(253, 251, 247, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(226, 232, 240, 0.5)',
        boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.08)' : 'none'
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '140px' }}>
          <a href="#hero" data-testid="nav-logo">
            <Logo />
          </a>

          {/* Desktop Navigation */}
          <div style={{ display: 'none', alignItems: 'center', gap: '8px' }} id="desktop-nav">
            {navLinks.map((link) => {
              const commonProps = {
                key: link.href,
                'data-testid': `nav-link-${link.href.replace(/[^a-zA-Z0-9]/g, '')}`,
                style: {
                  padding: '10px 20px',
                  borderRadius: '50px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#334155',
                  backgroundColor: 'transparent',
                  border: '1.5px solid transparent',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap',
                  textDecoration: 'none'
                },
                onMouseEnter: (e) => {
                  e.target.style.backgroundColor = 'rgba(245, 166, 35, 0.1)';
                  e.target.style.borderColor = 'rgba(245, 166, 35, 0.3)';
                  e.target.style.color = '#E67E22';
                },
                onMouseLeave: (e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.borderColor = 'transparent';
                  e.target.style.color = '#334155';
                }
              };
              return link.route ? (
                <Link {...commonProps} to={link.href}>{link.label}</Link>
              ) : (
                <a {...commonProps} href={link.href}>{link.label}</a>
              );
            })}
            <a
              href="#give"
              data-testid="nav-give-button"
              style={{
                padding: '12px 28px',
                borderRadius: '50px',
                fontSize: '14px',
                fontWeight: '600',
                color: '#0B192C',
                backgroundColor: '#F5A623',
                border: 'none',
                transition: 'all 0.3s ease',
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                marginLeft: '12px',
                boxShadow: '0 4px 15px rgba(245, 166, 35, 0.3)'
              }}
            >
              Give Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-button"
            style={{ display: 'block', padding: '10px', background: 'transparent', border: 'none', cursor: 'pointer' }}
            id="mobile-menu-btn"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <X size={28} color="#0B192C" /> : <Menu size={28} color="#0B192C" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ backgroundColor: '#FDFBF7', borderTop: '1px solid #E2E8F0' }}
            id="mobile-nav-menu"
          >
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {navLinks.map((link) => {
                const linkProps = {
                  key: link.href,
                  style: {
                    display: 'block',
                    padding: '14px 20px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '500',
                    color: '#334155',
                    backgroundColor: 'rgba(245, 166, 35, 0.05)',
                    textDecoration: 'none'
                  },
                  onClick: () => setIsOpen(false)
                };
                return link.route ? (
                  <Link {...linkProps} to={link.href}>{link.label}</Link>
                ) : (
                  <a {...linkProps} href={link.href}>{link.label}</a>
                );
              })}
              <a
                href="#give"
                style={{
                  display: 'block',
                  padding: '16px 24px',
                  borderRadius: '50px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#0B192C',
                  backgroundColor: '#F5A623',
                  textDecoration: 'none',
                  textAlign: 'center',
                  marginTop: '8px'
                }}
                onClick={() => setIsOpen(false)}
              >
                Give Now
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section - Quote Banner with Rich's image
const HeroSection = () => (
  <section id="hero" data-testid="hero-section" style={{ paddingTop: '140px' }}>
    {/* Quote Banner */}
    <div style={{
      backgroundColor: '#0B192C',
      padding: '80px 24px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
        gap: '48px',
        alignItems: 'center'
      }}>
        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 style={{
            fontFamily: "'Outfit', sans-serif",
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            fontWeight: '400',
            lineHeight: '1.4',
            color: '#FFFFFF',
            marginBottom: '32px',
            letterSpacing: '-0.01em'
          }}>
            "A Rich Life is marked by{' '}
            <span style={{ color: '#F5A623' }}>dignity</span>, meaningful{' '}
            <span style={{ color: '#F5A623' }}>connection</span>, real opportunity, and{' '}
            <span style={{ color: '#F5A623' }}>hope</span> for the future."
          </h1>
          <div>
            <p style={{ color: '#F5A623', fontWeight: '600', fontSize: '18px', marginBottom: '2px', lineHeight: '1.2' }}>Rich Salazar</p>
            <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '0', lineHeight: '1.2' }}>Founder, The Rich Porch</p>
          </div>
        </motion.div>

        {/* Rich's Image */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ display: 'flex', justifyContent: 'center' }}
        >
          <div style={{
            width: '440px',
            height: '520px',
            maxWidth: '100%',
            overflow: 'hidden',
            WebkitMaskImage: 'radial-gradient(ellipse 38% 52% at 50% 45%, rgba(0,0,0,1) 55%, rgba(0,0,0,0.8) 72%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)',
            maskImage: 'radial-gradient(ellipse 38% 52% at 50% 45%, rgba(0,0,0,1) 55%, rgba(0,0,0,0.8) 72%, rgba(0,0,0,0.35) 88%, rgba(0,0,0,0) 100%)'
          }}>
            <img 
              src={RICH_PHOTOS.main}
              alt="Rich Salazar"
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover', 
                objectPosition: 'center top'
              }}
            />
          </div>
        </motion.div>
      </div>
    </div>

    {/* CTA Section */}
    <div style={{
      backgroundColor: '#FDFBF7',
      padding: '60px 24px',
      textAlign: 'center'
    }}>
      <p style={{
        fontSize: '20px',
        color: '#64748b',
        marginBottom: '32px',
        fontStyle: 'italic'
      }}>
        One porch at a time.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
        <a 
          href="#get-help" 
          data-testid="hero-get-help-button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '18px 36px',
            backgroundColor: '#F5A623',
            color: '#0B192C',
            fontWeight: '600',
            fontSize: '16px',
            borderRadius: '60px',
            textDecoration: 'none',
            boxShadow: '0 8px 25px rgba(245, 166, 35, 0.35)'
          }}
        >
          Get Help <ArrowRight size={20} />
        </a>
        <a 
          href="#give" 
          data-testid="hero-donate-button"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '18px 36px',
            backgroundColor: '#0B192C',
            color: '#FFFFFF',
            fontWeight: '600',
            fontSize: '16px',
            borderRadius: '60px',
            textDecoration: 'none',
            boxShadow: '0 8px 25px rgba(11, 25, 44, 0.25)'
          }}
        >
          Donate Now <Heart size={20} />
        </a>
      </div>
    </div>
  </section>
);

// Why This Exists Section - NO "Our Purpose" header, NO video
const WhySection = () => (
  <section id="why" data-testid="why-section" style={{ padding: '96px 0', backgroundColor: '#FFFFFF' }}>
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '500',
          color: '#0B192C',
          marginBottom: '48px',
          textAlign: 'center'
        }}>
          Why The Rich Porch Exists
        </h2>

        {/* What is The Rich Porch */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.75rem', fontWeight: '500', color: '#0B192C', marginBottom: '20px' }}>
            What is The Rich Porch?
          </h3>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            In Scripture, the most notable and positive reference to a "porch" is Solomon's Porch, a public place where God's love and power were revealed to people through miracles. Today, many people are quietly struggling, hoping for a miracle. Financial pressure, loneliness, family stress, deep personal pain, and uncertainty about the future weigh on millions of lives.
          </p>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            The Rich Porch exists to help people move from survival to stability—and ultimately into a rich life: a life marked by dignity, meaningful connection, real opportunity, and hope for the future. Porch by porch.
          </p>
          <p style={{ color: '#F5A623', fontWeight: '600', fontSize: '18px' }}>
            This is what we mean by a rich life.
          </p>
        </div>

        {/* The Reality */}
        <div style={{ marginBottom: '48px', backgroundColor: '#FDFBF7', padding: '22px 26px', borderRadius: '14px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#0B192C', marginBottom: '12px' }}>
            The Reality
          </h3>
          <p style={{ color: '#475569', lineHeight: '1.75', margin: 0 }}>
            There are people all around us quietly falling apart. Not across the world. Not somewhere far away. Right here. In our neighborhoods. On our streets. Behind the doors we pass every day. A parent trying to hold everything together. A family one bill away from crisis. A person carrying the weight of past mistakes, trying to rebuild but running out of options. Someone sitting in silence, hoping—just once—that someone will notice. Most people don't talk about it. They carry it.
          </p>
        </div>

        {/* The Problem */}
        <div style={{ marginBottom: '48px', backgroundColor: '#FDFBF7', padding: '22px 26px', borderRadius: '14px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#0B192C', marginBottom: '12px' }}>
            The Problem
          </h3>
          <p style={{ color: '#475569', lineHeight: '1.75', margin: 0 }}>
            We live closer than ever before… but somehow, we've never been more disconnected. People want to help. They just don't know how. Others need help… but don't know where to turn without losing their dignity. And in between those two realities— nothing happens. Not because people don't care. But because there's no simple, trusted, human way to connect need with action.
          </p>
        </div>

        {/* The Belief */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#0B192C', marginBottom: '20px' }}>
            The Belief
          </h3>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '20px' }}>We believe something has to change. We believe:</p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[
              "No one should have to suffer alone while help lives next door.",
              "Dignity should never be the price someone pays to receive help.",
              "Communities are strongest when people take shared responsibility.",
              "Generosity should be simple, structured, and close to home."
            ].map((belief, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                <CheckCircle style={{ color: '#F5A623', flexShrink: 0, marginTop: '4px' }} size={20} />
                <span style={{ color: '#475569' }}>{belief}</span>
              </li>
            ))}
          </ul>
          <p style={{ color: '#0B192C', fontWeight: '600', marginTop: '20px', fontSize: '18px' }}>
            And most of all… We believe people still care—when given a clear way to act.
          </p>
        </div>

        {/* The Vision */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#0B192C', marginBottom: '16px' }}>
            The Vision
          </h3>
          <p style={{ color: '#475569', lineHeight: '1.8' }}>
            There was a time when the front porch meant something. It was where people gathered. Where neighbors noticed each other. Where conversations happened. Where community lived. The porch wasn't just part of a house. It was a symbol of connection. The Rich Porch exists to bring that spirit back. Not just as an idea— but as a movement.
          </p>
        </div>

        {/* The Mission */}
        <div style={{ marginBottom: '48px', backgroundColor: '#0B192C', padding: '40px', borderRadius: '16px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#FFFFFF', marginBottom: '16px' }}>
            The Mission
          </h3>
          <p style={{ color: '#CBD5E1', lineHeight: '1.8', marginBottom: '20px' }}>
            The Rich Porch exists to help people move from survival to stability—through practical support, human connection, and organized generosity. This isn't about charity at a distance. This is about:
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {["Showing up", "Meeting real needs", "Restoring dignity", "Creating pathways forward"].map((item, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#F5A623' }} />
                <span style={{ color: '#FFFFFF', fontWeight: '500' }}>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* The Conviction */}
        <div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#0B192C', marginBottom: '16px' }}>
            The Conviction
          </h3>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            Too many people are waiting for a miracle. Not a metaphor. A real one. A moment where something changes. Where pressure lifts. Where hope returns. Where someone shows up. The Rich Porch exists to make that moment possible— again and again— right where people live.
          </p>
          <p style={{ color: '#F5A623', fontWeight: '600', fontSize: '18px', fontStyle: 'italic' }}>
            Because a rich life should not be out of reach. And a miracle should not be far from home.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

// What We Do Section - NO "Our Approach" header, NO video, all text
const WhatWeDoSection = () => (
  <section id="what-we-do" data-testid="what-we-do-section" style={{ padding: '96px 0', backgroundColor: '#FDFBF7' }}>
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '500',
          color: '#0B192C',
          marginBottom: '48px',
          textAlign: 'center'
        }}>
          What We Do
        </h2>

        {/* How We Show Up */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.75rem', fontWeight: '500', color: '#0B192C', marginBottom: '20px' }}>
            1. How We Show Up
          </h3>
          <p style={{ color: '#F5A623', fontWeight: '600', fontSize: '18px', marginBottom: '16px' }}>
            We turn compassion into practical action. We show up on your porch.
          </p>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            The Rich Porch exists to meet real needs in real time—through a model that is simple, structured, and centered on people. This is not random charity. This is intentional, organized care designed to help people move forward.
          </p>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            We focus on two things at the same time: Immediate relief for people in crisis and forward movement toward stability. Because people don't just need help getting through the moment— they need a path beyond it. We don't start with assumptions. We start with people.
          </p>
        </div>

        {/* We Lead With Curiosity */}
        <div style={{ marginBottom: '48px', backgroundColor: '#FFFFFF', padding: '32px', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
          <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', fontWeight: '500', color: '#0B192C', marginBottom: '16px' }}>
            We Lead With Curiosity
          </h4>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            Every conversation begins with one simple goal: to understand.
          </p>
          <p style={{ color: '#64748b', fontStyle: 'italic', backgroundColor: '#FDFBF7', padding: '16px', borderRadius: '8px', borderLeft: '3px solid #F5A623' }}>
            "Hi, I've been meeting people in the area to understand what people are personally going through. If you're open to it, I'd love to hear a little about your experience."
          </p>
          <p style={{ color: '#475569', lineHeight: '1.8', marginTop: '16px' }}>
            Most people aren't used to being asked this. But when they are, something opens.
          </p>
        </div>

        {/* We Ask What Actually Matters */}
        <div style={{ marginBottom: '48px' }}>
          <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', fontWeight: '500', color: '#0B192C', marginBottom: '16px' }}>
            We Ask What Actually Matters
          </h4>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            We don't stay on the surface. We ask real questions about real life:
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {[
              "What's been weighing on you the most lately?",
              "What has been the hardest part of this past year?",
              "What do you wish people understood about what you're going through?",
              "What kind of support would make your life easier right now?"
            ].map((q, i) => (
              <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                <ChevronRight style={{ color: '#F5A623', flexShrink: 0, marginTop: '4px' }} size={18} />
                <span style={{ color: '#475569' }}>{q}</span>
              </li>
            ))}
          </ul>
          <p style={{ color: '#0B192C', fontWeight: '500', marginTop: '16px' }}>
            Because real help starts with real understanding.
          </p>
        </div>

        {/* We Create Space */}
        <div style={{ marginBottom: '48px', backgroundColor: '#FFFFFF', padding: '32px', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
          <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', fontWeight: '500', color: '#0B192C', marginBottom: '16px' }}>
            We Create Space for People to Be Honest
          </h4>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            People don't open up unless they feel safe. So we show up: calm, present, without judgment, without pressure, without trying to immediately fix everything.
          </p>
          <p style={{ color: '#64748b', fontStyle: 'italic' }}>
            Sometimes the most powerful thing we can say is: "That sounds really hard." And then… we listen.
          </p>
        </div>

        {/* What Helps Right Now */}
        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.75rem', fontWeight: '500', color: '#0B192C', marginBottom: '20px' }}>
            2. We Focus on What Helps Right Now
          </h3>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            We ask one simple question: <strong>"What would help you most right now?"</strong>
          </p>
          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px' }}>
            Sometimes the answer is practical: food, transportation, help navigating resources, short-term financial support. Other times, the answer is simply being heard. Both matter.
          </p>
        </div>

        {/* Connect People */}
        <div style={{ backgroundColor: '#0B192C', padding: '40px', borderRadius: '16px' }}>
          <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', fontWeight: '500', color: '#FFFFFF', marginBottom: '16px' }}>
            We Connect People to Real Support
          </h4>
          <p style={{ color: '#CBD5E1', lineHeight: '1.8', marginBottom: '20px' }}>
            We don't try to do everything ourselves. We help connect people to what already exists:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
            <div>
              <h5 style={{ color: '#F5A623', fontWeight: '600', marginBottom: '12px' }}>Practical Support</h5>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {["Food assistance", "Housing support", "Local aid organizations", "Community groups"].map((item, i) => (
                  <li key={i} style={{ color: '#CBD5E1', marginBottom: '8px', fontSize: '14px' }}>• {item}</li>
                ))}
              </ul>
            </div>
            <div>
              <h5 style={{ color: '#F5A623', fontWeight: '600', marginBottom: '12px' }}>Emotional & Crisis Support</h5>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {["Counseling services", "Crisis resources", "Recovery programs", "Safe support systems"].map((item, i) => (
                  <li key={i} style={{ color: '#CBD5E1', marginBottom: '8px', fontSize: '14px' }}>• {item}</li>
                ))}
              </ul>
            </div>
          </div>
          <p style={{ color: '#94a3b8', marginTop: '20px', fontStyle: 'italic' }}>
            Because help should be accessible—not complicated.
          </p>
        </div>

        <p style={{ textAlign: 'center', marginTop: '48px', color: '#F5A623', fontWeight: '600', fontSize: '18px' }}>
          This is how we help. One conversation at a time. One person at a time. One porch at a time.
        </p>
      </motion.div>
    </div>
  </section>
);

// Get Help Section - Full copy from document
const GetHelpSection = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    city: '',
    situation: '',
    help_needed: '',
    urgency: 'not_urgent',
    contact_preference: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/help-request`, formData);
      setSubmitted(true);
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error. Please try again.');
    }
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    fontSize: '16px',
    fontFamily: "'Outfit', sans-serif",
    backgroundColor: '#FFFFFF',
    outline: 'none'
  };

  if (submitted) {
    return (
      <section id="get-help" data-testid="get-help-section" style={{ padding: '96px 0', backgroundColor: '#FFFFFF' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ background: 'linear-gradient(135deg, rgba(245, 166, 35, 0.1) 0%, rgba(230, 126, 34, 0.05) 100%)', borderRadius: '24px', padding: '64px 48px' }}>
            <CheckCircle style={{ color: '#F5A623', margin: '0 auto 24px' }} size={64} />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem', fontWeight: '500', color: '#0B192C', marginBottom: '16px' }}>
              Thank You for Reaching Out
            </h2>
            <p style={{ color: '#64748b', fontSize: '18px' }}>
              You are not alone. And this may be the first step forward.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="get-help" data-testid="get-help-section" style={{ padding: '96px 0', backgroundColor: '#FFFFFF' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '500',
            color: '#0B192C',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            Need Help?
          </h2>

          <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '16px', textAlign: 'center', maxWidth: '700px', margin: '0 auto 32px' }}>
            If you're going through something difficult, you're not alone. Life can get heavy—financial pressure, personal challenges, unexpected situations. Sometimes it feels like everything is piling up at once. The Rich Porch exists for moments like this.
          </p>

          <div style={{ backgroundColor: '#FDFBF7', padding: '32px', borderRadius: '16px', marginBottom: '48px' }}>
            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#0B192C', marginBottom: '16px' }}>
              You Don't Have To Have It All Figured Out
            </h3>
            <p style={{ color: '#475569', lineHeight: '1.8' }}>
              You don't need the perfect words. You don't need a perfect situation. You don't even need to know exactly what kind of help you need. You just need a place to start.
            </p>
          </div>

          <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', fontWeight: '500', color: '#0B192C', marginBottom: '24px' }}>
            What To Expect:
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px', marginBottom: '48px' }}>
            {[
              { step: "1", title: "We Listen", desc: "We take time to understand what you're going through—without judgment or pressure." },
              { step: "2", title: "We Clarify", desc: "We'll ask a few simple questions to understand what might help most right now." },
              { step: "3", title: "We Review", desc: "If financial or practical support is requested, we review needs based on urgency and available resources." },
              { step: "4", title: "We Respond", desc: "We do our best to connect you with the right kind of help—whether that's direct support, resources, or next steps." }
            ].map((item, index) => (
              <div key={index} style={{ textAlign: 'center' }}>
                <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#F5A623', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                  <span style={{ color: '#0B192C', fontWeight: '700', fontSize: '18px' }}>{item.step}</span>
                </div>
                <h4 style={{ fontWeight: '600', color: '#0B192C', marginBottom: '8px' }}>{item.title}</h4>
                <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6' }}>{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} data-testid="help-request-form" style={{ backgroundColor: '#FDFBF7', borderRadius: '24px', padding: '40px', border: '1px solid #E2E8F0' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>Full Name *</label>
                <input type="text" name="full_name" required style={inputStyle} placeholder="Your name" value={formData.full_name} onChange={handleChange} data-testid="input-full-name" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>Email *</label>
                  <input type="email" name="email" required style={inputStyle} placeholder="your@email.com" value={formData.email} onChange={handleChange} data-testid="input-email" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>Phone Number</label>
                  <input type="tel" name="phone" style={inputStyle} placeholder="(555) 123-4567" value={formData.phone} onChange={handleChange} data-testid="input-phone" />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>City / Neighborhood</label>
                <input type="text" name="city" style={inputStyle} placeholder="Where are you located?" value={formData.city} onChange={handleChange} data-testid="input-city" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>What's been going on? *</label>
                <textarea name="situation" required rows={4} style={{ ...inputStyle, resize: 'none' }} placeholder="Share as much or as little as you're comfortable with..." value={formData.situation} onChange={handleChange} data-testid="input-situation" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>What would help you most right now? *</label>
                <textarea name="help_needed" required rows={3} style={{ ...inputStyle, resize: 'none' }} placeholder="Food, transportation, financial support, someone to talk to..." value={formData.help_needed} onChange={handleChange} data-testid="input-help-needed" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>Timeline / Urgency</label>
                  <select name="urgency" style={inputStyle} value={formData.urgency} onChange={handleChange} data-testid="select-urgency">
                    <option value="not_urgent">Not Urgent</option>
                    <option value="soon">Need Help Soon</option>
                    <option value="urgent">Urgent</option>
                    <option value="crisis">Crisis - Need Help Now</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#0B192C', marginBottom: '8px' }}>Preferred way to be contacted</label>
                  <select name="contact_preference" style={inputStyle} value={formData.contact_preference} onChange={handleChange} data-testid="select-contact-preference">
                    <option value="email">Email</option>
                    <option value="phone">Phone Call</option>
                    <option value="text">Text Message</option>
                  </select>
                </div>
              </div>
              <button type="submit" disabled={isSubmitting} data-testid="submit-help-request" style={{
                width: '100%', padding: '18px', backgroundColor: '#F5A623', color: '#0B192C', fontWeight: '600', fontSize: '16px', borderRadius: '50px', border: 'none', cursor: 'pointer', marginTop: '8px', boxShadow: '0 4px 15px rgba(245, 166, 35, 0.3)'
              }}>
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>

          <p style={{ textAlign: 'center', fontSize: '14px', color: '#94a3b8', marginTop: '24px', fontStyle: 'italic' }}>
            Your information will be handled with care and respect. We do not share your story publicly without your permission.
          </p>
          <p style={{ textAlign: 'center', fontSize: '14px', color: '#ef4444', marginTop: '16px' }}>
            <strong>Note:</strong> If you're in immediate danger or crisis, please contact local emergency services or a crisis hotline.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Events Section
const EventsSection = () => {
  return (
    <section id="events" data-testid="events-section" style={{ padding: '96px 0', backgroundColor: '#FDFBF7' }}>
      <div style={{ backgroundColor: '#0B192C', padding: '16px 0', marginBottom: '64px' }}>
        <Marquee speed={40} gradient={false}>
          {["JOIN US ON THE PORCH", "COMMUNITY EVENTS", "MAKING A DIFFERENCE TOGETHER"].map((text, i) => (
            <span key={i} style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', margin: '0 32px' }}>★ {text}</span>
          ))}
        </Marquee>
      </div>

      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px' }}>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '500', color: '#0B192C', textAlign: 'center', marginBottom: '32px' }}>
          Events
        </h2>

        <div style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '16px',
          padding: '56px 32px',
          textAlign: 'center',
          maxWidth: '640px',
          margin: '0 auto'
        }}>
          <Calendar size={42} style={{ color: '#F5A623', margin: '0 auto 18px' }} />
          <p style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(1.25rem, 2.2vw, 1.6rem)',
            color: '#0B192C',
            fontStyle: 'italic',
            margin: 0,
            lineHeight: '1.5'
          }}>
            Upcoming events will be announced shortly.
          </p>
        </div>
      </div>
    </section>
  );
};

// Give Section - Monthly: $10, $20, $40, $50, $100, Other + One-time gift
const GiveSection = () => {
  const [isMonthly, setIsMonthly] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState(20);
  const monthlyAmounts = [10, 20, 40, 50, 100];
  const oneTimeAmounts = [25, 50, 100, 250, 500];

  return (
    <section id="give" data-testid="give-section" style={{ padding: '96px 0', backgroundColor: '#0B192C' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 450px), 1fr))', gap: '64px', alignItems: 'start' }}>
          {/* Left - Content */}
          <div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '500', color: '#FFFFFF', marginBottom: '24px' }}>
              Give
            </h2>
            <p style={{ fontSize: '18px', color: '#CBD5E1', marginBottom: '32px', lineHeight: '1.7' }}>
              The Rich Porch focuses on everything beyond emergency financial help.
            </p>

            <h3 style={{ color: '#F5A623', fontWeight: '600', marginBottom: '20px' }}>What Your Giving Supports:</h3>
            <p style={{ color: '#CBD5E1', lineHeight: '1.7', marginBottom: '20px' }}>
              The Rich Porch focuses on helping people rebuild—not just get through a crisis. While some programs provide direct financial assistance for urgent needs, The Rich Porch is designed to support what comes next. Your giving helps fund:
            </p>
            {[
              "Mentorship and life guidance",
              "Spiritual guidance and mentorship",
              "Financial coaching and direction",
              "Education and trade opportunities",
              "Real relationships and ongoing support"
            ].map((item, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <CheckCircle style={{ color: '#F5A623' }} size={20} />
                <span style={{ color: '#CBD5E1' }}>{item}</span>
              </div>
            ))}
            <p style={{ color: '#94a3b8', marginTop: '20px', fontStyle: 'italic' }}>
              Because rebuilding a life isn't just financial—it's personal, emotional, and spiritual.
            </p>
          </div>

          {/* Right - Donation Box */}
          <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', borderRadius: '24px', padding: '40px', border: '1px solid rgba(255,255,255,0.1)' }}>
            {/* Toggle Monthly / One-Time */}
            <div style={{ display: 'flex', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '50px', padding: '4px', marginBottom: '32px' }}>
              <button
                onClick={() => { setIsMonthly(true); setSelectedAmount(20); }}
                style={{
                  flex: 1, padding: '12px', borderRadius: '50px', border: 'none', cursor: 'pointer', fontWeight: '600',
                  backgroundColor: isMonthly ? '#F5A623' : 'transparent',
                  color: isMonthly ? '#0B192C' : '#FFFFFF'
                }}
              >
                Monthly
              </button>
              <button
                onClick={() => { setIsMonthly(false); setSelectedAmount(50); }}
                style={{
                  flex: 1, padding: '12px', borderRadius: '50px', border: 'none', cursor: 'pointer', fontWeight: '600',
                  backgroundColor: !isMonthly ? '#F5A623' : 'transparent',
                  color: !isMonthly ? '#0B192C' : '#FFFFFF'
                }}
              >
                One-Time
              </button>
            </div>

            <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.5rem', color: '#FFFFFF', marginBottom: '24px', textAlign: 'center' }}>
              {isMonthly ? 'Choose Monthly Amount' : 'Choose One-Time Amount'}
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
              {(isMonthly ? monthlyAmounts : oneTimeAmounts).map((amount) => (
                <button
                  key={amount}
                  onClick={() => setSelectedAmount(amount)}
                  data-testid={`donation-amount-${amount}`}
                  style={{
                    padding: '16px 8px', borderRadius: '12px', fontWeight: '600', fontSize: '16px', border: 'none', cursor: 'pointer',
                    backgroundColor: selectedAmount === amount ? '#F5A623' : 'rgba(255,255,255,0.1)',
                    color: selectedAmount === amount ? '#0B192C' : '#FFFFFF'
                  }}
                >
                  ${amount}{isMonthly ? '/mo' : ''}
                </button>
              ))}
              <button
                onClick={() => setSelectedAmount(0)}
                style={{
                  padding: '16px 8px', borderRadius: '12px', fontWeight: '600', fontSize: '16px', border: 'none', cursor: 'pointer',
                  backgroundColor: selectedAmount === 0 ? '#F5A623' : 'rgba(255,255,255,0.1)',
                  color: selectedAmount === 0 ? '#0B192C' : '#FFFFFF'
                }}
              >
                Other
              </button>
            </div>

            {selectedAmount === 0 && (
              <div style={{ marginBottom: '24px' }}>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>$</span>
                  <input
                    type="number"
                    placeholder="Enter amount"
                    data-testid="donation-custom-amount"
                    style={{ width: '100%', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', padding: '16px 16px 16px 32px', color: '#FFFFFF', fontSize: '16px', outline: 'none' }}
                  />
                </div>
              </div>
            )}

            <button data-testid="donate-button" style={{
              width: '100%', backgroundColor: '#F5A623', color: '#0B192C', padding: '18px', borderRadius: '50px', fontWeight: '600', fontSize: '18px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', boxShadow: '0 8px 25px rgba(245, 166, 35, 0.35)'
            }}>
              <Heart size={22} /> {isMonthly ? `Give $${selectedAmount || '...'}/month` : `Donate $${selectedAmount || '...'}`}
            </button>

            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '14px', marginTop: '16px' }}>
              Secure donation processing
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Founder Section - Updated with new photos and full copy
const FounderSection = () => (
  <section id="founder" data-testid="founder-section" style={{ padding: '96px 0', backgroundColor: '#FFFFFF' }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))', gap: '64px', alignItems: 'center' }}>
        {/* Left - Image */}
        <div style={{ position: 'relative' }}>
          <div style={{ aspectRatio: '4/5', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.15)' }}>
            <img src={RICH_PHOTOS.seated} alt="Rich Salazar - Founder" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>

        {/* Right - Content */}
        <div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '500', color: '#0B192C', marginBottom: '24px' }}>
            Meet the Founder
          </h2>
          <p style={{ fontSize: '16px', color: '#475569', marginBottom: '20px', lineHeight: '1.8' }}>
            Rich Salazar is a transformational humanitarian and the founder of Rich Global, The Rich Revolution, The Rich Porch, and Twenty Together. Rich has spent years working closely with people, families, and communities navigating real-life challenges.
          </p>
          <p style={{ fontSize: '16px', color: '#475569', marginBottom: '20px', lineHeight: '1.8' }}>
            His work is centered on one idea: helping people live with dignity, purpose, and real support—especially those who feel like they've fallen behind or made mistakes and need a way forward.
          </p>
          <p style={{ fontSize: '16px', color: '#475569', marginBottom: '32px', lineHeight: '1.8' }}>
            For Rich, The Rich Porch is personal. He has seen what it looks like when people are left to carry heavy things alone. He has also seen what happens when someone shows up with compassion, consistency, and care. That belief is what drives everything behind The Rich Porch.
          </p>

          <div style={{ backgroundColor: '#FDFBF7', borderRadius: '16px', padding: '24px', border: '1px solid #E2E8F0' }}>
            <h4 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.25rem', fontWeight: '500', color: '#0B192C', marginBottom: '12px' }}>Why He Started This:</h4>
            <p style={{ color: '#475569', lineHeight: '1.7', marginBottom: '16px' }}>
              Rich began to see a gap that he couldn't ignore. People want to help—but don't know how. People need help—but don't know where to turn without losing dignity.
            </p>
            <p style={{ color: '#475569', lineHeight: '1.7', marginBottom: '16px' }}>
              There was something else he couldn't ignore. In many places, the systems that were once closest to the community—organizations, institutions, even faith communities—have become distant from the everyday struggles people are facing. Not intentionally. But over time, something shifted. People are still hurting. But fewer people are being reached in real, personal ways.
            </p>
            <p style={{ color: '#F5A623', fontWeight: '600' }}>
              The Rich Porch exists to help close that gap. Not by replacing anything— but by restoring something simple: People showing up for people.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Sponsors Section
const SponsorsSection = () => (
  <section id="sponsors" data-testid="sponsors-section" style={{ padding: '96px 0', backgroundColor: '#FDFBF7' }}>
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
      <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: '500', color: '#0B192C', marginBottom: '24px' }}>
        Sponsors
      </h2>
      <p style={{ color: '#64748b', marginBottom: '48px' }}>
        We're grateful for the organizations that support our mission to help people live a rich life.
      </p>
      <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: '48px', border: '1px solid #E2E8F0' }}>
        <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sponsor logos coming soon</p>
      </div>
      <a href="#" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#E67E22', fontWeight: '500', textDecoration: 'none', marginTop: '32px' }}>
        Become a Sponsor <ChevronRight size={16} />
      </a>
    </div>
  </section>
);

// Footer - Updated with TikTok
const Footer = () => (
  <footer data-testid="footer" style={{ backgroundColor: '#0B192C', padding: '80px 0 40px' }}>
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '48px', marginBottom: '48px' }}>
        <div style={{ gridColumn: 'span 2' }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2rem, 3vw, 2.5rem)', fontWeight: '600', color: '#FFFFFF', marginBottom: '16px' }}>
            THE RICH PORCH
          </h2>
          <p style={{ color: '#94a3b8', marginBottom: '24px', maxWidth: '400px' }}>
            A Miracle on Every Porch. Helping people move from survival to stability—one porch at a time.
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            {[
              { icon: Facebook, id: 'facebook' },
              { icon: Instagram, id: 'instagram' },
              { icon: TikTok, id: 'tiktok' },
              { icon: () => <span style={{ fontWeight: 'bold', fontSize: '14px' }}>X</span>, id: 'x' },
              { icon: Youtube, id: 'youtube' }
            ].map(({ icon: Icon, id }) => (
              <a key={id} href="#" data-testid={`social-${id}`} style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>
                <Icon size={20} />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 style={{ color: '#FFFFFF', fontWeight: '500', marginBottom: '20px' }}>Quick Links</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {["Why This Exists", "What We Do", "Get Help", "Events", "Give"].map((link) => (
              <li key={link} style={{ marginBottom: '12px' }}>
                <a href={`#${link.toLowerCase().replace(/\s+/g, '-')}`} style={{ color: '#94a3b8', textDecoration: 'none' }}>{link}</a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 style={{ color: '#FFFFFF', fontWeight: '500', marginBottom: '20px' }}>Contact</h4>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#94a3b8', marginBottom: '12px' }}>
              <Mail size={16} color="#F5A623" />
              <a href="mailto:hello@therichporch.org" style={{ color: '#94a3b8', textDecoration: 'none' }}>hello@therichporch.org</a>
            </li>
            <li style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#94a3b8' }}>
              <Phone size={16} color="#F5A623" />
              <a href="tel:+15551234567" style={{ color: '#94a3b8', textDecoration: 'none' }}>(555) 123-4567</a>
            </li>
          </ul>
        </div>
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '32px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <p style={{ color: '#64748b', fontSize: '14px' }}>© 2026 The Rich Porch. All rights reserved.</p>
        <div style={{ display: 'flex', gap: '24px' }}>
          <a href="#" style={{ color: '#64748b', fontSize: '14px', textDecoration: 'none' }}>Privacy Policy</a>
          <a href="#" style={{ color: '#64748b', fontSize: '14px', textDecoration: 'none' }}>Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);

// Home Page Layout
const HomePage = () => (
  <div className="App">
    <div className="noise-overlay" />
    <Navigation />
    <main>
      <HeroSection />
      <WhySection />
      <WhatWeDoSection />
      <GetHelpSection />
      <EventsSection />
      <GiveSection />
      <FounderSection />
      <SponsorsSection />
    </main>
    <Footer />
  </div>
);

// Main App
function App() {
  const isUnderConstruction =
    process.env.REACT_APP_UNDER_CONSTRUCTION === "true";

  if (isUnderConstruction) {
    return <UnderConstruction />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/volunteer" element={<VolunteerPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
